import { RouteObject, useRoutes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { Shell } from '@/components/shell/shell';
import { DogsList } from '@/features/dogs/list';
import { LittersList } from '@/features/litters/list';
import { UsersList } from '@/features/users/list';
import { useCognitoAuth } from '../hooks/useCognitoAuth';
import { PageLoader } from '../components/page-loader';
import { LoginPage } from '../features/login';
import { ErrorDisplay } from '@/components/ui/error-display';

const protectedRoutes: RouteObject[] = [
  {
    path: '/',
    element: <Navigate to="/users" replace />,
  },
  {
    path: '/dogs',
    element: (
      <Shell>
        <DogsList />
      </Shell>
    ),
  },
  {
    path: '/litters',
    element: (
      <Shell>
        <LittersList />
      </Shell>
    ),
  },
  {
    path: '/users',
    element: (
      <Shell>
        <UsersList />
      </Shell>
    ),
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
];

const publicRoutes: RouteObject[] = [
  {
    path: '*',
    element: (
      <Shell showSidebar={false}>
        <LoginPage />
      </Shell>
    ),
  },
];

export function AppRoutes() {
  const { isLoading, isAuthenticated, error } = useCognitoAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const routes = isAuthenticated ? protectedRoutes : publicRoutes;

  // Redirect authenticated users away from login page
  // Only redirect if we're not in the middle of an OAuth callback
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      // Check if we're in the middle of an OAuth callback (has code param)
      const urlParams = new URLSearchParams(window.location.search);
      const hasOAuthCode = urlParams.has('code') || urlParams.has('error');

      // Don't redirect during OAuth callback - let the auth hook handle it
      if (hasOAuthCode) {
        return;
      }

      // If on login page or unmatched route, redirect to home
      const isProtectedRoute = ['/', '/dogs', '/litters', '/users'].includes(location.pathname);
      if (!isProtectedRoute) {
        navigate('/', { replace: true });
      }
    }
  }, [isAuthenticated, isLoading, location.pathname, navigate]);

  const element = useRoutes(routes);

  if (isLoading) {
    return <PageLoader />;
  }

  // Only show errors if user is authenticated but there's still an error
  // If not authenticated, just show login page (errors from getCurrentUser are expected)
  if (error && isAuthenticated) {
    return (
      <Shell>
        <div className="container mx-auto py-8">
          <ErrorDisplay
            error={error}
            onRetry={() => {
              // Reload the page to retry authentication
              window.location.reload();
            }}
            retryLabel="Reload Page"
            showDetails={true}
          />
        </div>
      </Shell>
    );
  }

  return element;
}
